import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Phone, PhoneOff, PhoneCall, PhoneIncoming, PhoneMissed,
  Mic, MicOff, Pause, Play,
  X, Minimize2, Maximize2, Users, History,
  Building2, Headphones, PhoneForwarded, Hash,
  Loader2, CheckCircle, XCircle, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/App';

// Department icons mapping
const DEPARTMENT_ICONS = {
  'Reception': Building2,
  'Front Desk': Building2,
  'Room Service': Headphones,
  'Housekeeping': Users,
  'Concierge': PhoneForwarded,
  'Restaurant': Headphones,
  'Spa': Headphones,
  'default': Phone
};

const getDepartmentIcon = (department) => {
  return DEPARTMENT_ICONS[department] || DEPARTMENT_ICONS.default;
};

// Format duration
const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const MizuDialer = ({ 
  hotelId, 
  isPortal = false, 
  sipConfig: propSipConfig = null,  // SIP config passed from parent (for guest portal)
  guestCredentials = null,
  onClose 
}) => {
  // State
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [activeTab, setActiveTab] = useState('directory');
  const [sipConfig, setSipConfig] = useState(propSipConfig);
  const [directory, setDirectory] = useState([]);
  const [callLogs, setCallLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Mizu webphone state
  const [webphoneLoaded, setWebphoneLoaded] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [inCall, setInCall] = useState(false);
  const [callState, setCallState] = useState('idle'); // idle, ringing, connected, ended
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [dialNumber, setDialNumber] = useState('');
  const [callerInfo, setCallerInfo] = useState(null);
  const [registrationError, setRegistrationError] = useState(null);
  
  // Refs
  const webphoneRef = useRef(null);
  const callTimerRef = useRef(null);
  const iframeRef = useRef(null);

  // Update sipConfig when prop changes
  useEffect(() => {
    if (propSipConfig) {
      setSipConfig(propSipConfig);
    }
  }, [propSipConfig]);

  // Fetch SIP config and directory (only if sipConfig not passed as prop)
  useEffect(() => {
    if (hotelId) {
      fetchSIPData();
    }
  }, [hotelId]);

  // Call duration timer
  useEffect(() => {
    if (callState === 'connected') {
      callTimerRef.current = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    } else {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
      }
      if (callState === 'idle') {
        setCallDuration(0);
      }
    }
    return () => {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
      }
    };
  }, [callState]);

  // Load Mizu webphone script
  useEffect(() => {
    if (isOpen && sipConfig && !webphoneLoaded) {
      loadMizuWebphone();
    }
  }, [isOpen, sipConfig]);

  const fetchSIPData = async () => {
    try {
      setLoading(true);
      
      // Fetch directory (always needed for directory listing)
      const directoryRes = await api.get(`/sip/public/directory/${hotelId}`).catch(() => ({ data: [] }));
      setDirectory(directoryRes.data || []);
      
      // Only fetch SIP config if not passed as prop
      if (!propSipConfig) {
        const configRes = await api.get(`/sip/public/config/${hotelId}`).catch(() => ({ data: null }));
        if (configRes.data) {
          setSipConfig(configRes.data);
        }
      }
      
      // Fetch call logs if authenticated
      if (!isPortal || guestCredentials) {
        try {
          const logsRes = await api.get('/sip/call-logs?limit=20');
          setCallLogs(logsRes.data?.logs || []);
        } catch (e) {
          console.log('Could not fetch call logs');
        }
      }
    } catch (error) {
      console.error('Failed to fetch SIP data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMizuWebphone = useCallback(() => {
    if (webphoneLoaded || !sipConfig || !guestCredentials) return;
    
    // Check if webphone_api is already loaded
    if (window.webphone_api) {
      initializeMizuWebphone();
      return;
    }
    
    // Load the Mizu webphone script
    const script = document.createElement('script');
    script.src = '/webphone/webphone/webphone_api.js';
    script.async = true;
    script.onload = () => {
      console.log('Mizu webphone API loaded');
      setWebphoneLoaded(true);
      initializeMizuWebphone();
    };
    script.onerror = () => {
      console.error('Failed to load Mizu webphone API');
      toast.error('Failed to load phone system');
    };
    document.body.appendChild(script);
  }, [sipConfig, guestCredentials, webphoneLoaded]);

  const initializeMizuWebphone = useCallback(() => {
    if (!window.webphone_api || !sipConfig || !guestCredentials) {
      console.log('Cannot initialize: missing webphone_api, sipConfig, or guestCredentials');
      return;
    }
    
    setIsConnecting(true);
    setRegistrationError(null);
    
    try {
      const wp = window.webphone_api;
      webphoneRef.current = wp;
      
      // Set up callbacks
      wp.onAppStateChange((state) => {
        console.log('Mizu App State:', state);
        if (state === 'loaded') {
          // Configure the webphone for SIP over WebSocket
          // serveraddress: SIP server domain (with optional port)
          const sipServer = sipConfig.sip_port && sipConfig.sip_port !== 5060 
            ? `${sipConfig.sip_domain}:${sipConfig.sip_port}` 
            : sipConfig.sip_domain;
          
          wp.setparameter('serveraddress', sipServer, false);
          
          // webrtcserveraddress: WebSocket URL for SIP signaling (NOT WebRTC, just SIP over WS)
          if (sipConfig.websocket_url) {
            wp.setparameter('webrtcserveraddress', sipConfig.websocket_url, false);
          }
          
          // SIP credentials
          wp.setparameter('username', guestCredentials.username, false);
          wp.setparameter('password', guestCredentials.password, false);
          wp.setparameter('displayname', guestCredentials.display_name || 'Guest', false);
          
          // Additional settings
          wp.setparameter('loglevel', '3', false);
          wp.setparameter('autostart', 'true', false);
          
          console.log('Mizu webphone configured:', {
            server: sipServer,
            websocket: sipConfig.websocket_url,
            username: guestCredentials.username,
            displayName: guestCredentials.display_name
          });
          
          // Start the webphone
          wp.start();
        } else if (state === 'started') {
          console.log('Mizu webphone started, registering...');
          wp.register();
        }
      });
      
      wp.onRegStateChange((state, reason) => {
        console.log('Mizu Reg State:', state, reason);
        setIsConnecting(false);
        
        if (state === 'registered') {
          setIsRegistered(true);
          setRegistrationError(null);
          toast.success('Connected to phone system');
        } else if (state === 'unregistered') {
          setIsRegistered(false);
        } else if (state === 'failed') {
          setIsRegistered(false);
          setRegistrationError(reason || 'Registration failed');
          toast.error(`Phone connection failed: ${reason || 'Unknown error'}`);
        }
      });
      
      wp.onCallStateChange((event, direction, peername, peerdisplayname, line, callid) => {
        console.log('Mizu Call State:', event, direction, peername, peerdisplayname);
        
        if (event === 'callSetup' || event === 'setup') {
          setCallState('ringing');
          setInCall(true);
          setCallerInfo({
            name: peerdisplayname || directory.find(d => d.extension === peername)?.name || peername,
            number: peername,
            direction: direction === 1 ? 'outbound' : 'inbound'
          });
        } else if (event === 'callRinging' || event === 'ringing') {
          setCallState('ringing');
        } else if (event === 'callConnected' || event === 'connected') {
          setCallState('connected');
          setInCall(true);
        } else if (event === 'callDisconnected' || event === 'disconnected') {
          setCallState('idle');
          setInCall(false);
          setCallerInfo(null);
          setIsMuted(false);
          setIsOnHold(false);
          setCallDuration(0);
        }
      });
      
    } catch (error) {
      console.error('Failed to initialize Mizu webphone:', error);
      setIsConnecting(false);
      setRegistrationError(error.message);
      toast.error('Failed to initialize phone system');
    }
  }, [sipConfig, guestCredentials, directory]);

  // Make outbound call
  const makeCall = (target) => {
    if (!webphoneRef.current || !isRegistered) {
      toast.error('Not connected to phone system');
      return;
    }
    
    try {
      const entry = directory.find(d => d.extension === target);
      setCallerInfo({
        name: entry?.name || target,
        number: target,
        direction: 'outbound'
      });
      setCallState('ringing');
      
      webphoneRef.current.call(target);
      
      // Log the call
      if (!isPortal) {
        api.post('/sip/call-logs', {
          caller_id: guestCredentials?.username || 'guest',
          callee_id: target,
          callee_name: entry?.name,
          direction: 'outbound',
          status: 'ringing'
        }).catch(() => {});
      }
    } catch (error) {
      console.error('Failed to make call:', error);
      toast.error('Failed to make call');
      setCallState('idle');
    }
  };

  // Hangup call
  const hangupCall = () => {
    if (webphoneRef.current) {
      webphoneRef.current.hangup();
    }
  };

  // Accept incoming call
  const acceptCall = () => {
    if (webphoneRef.current) {
      webphoneRef.current.accept();
    }
  };

  // Reject incoming call
  const rejectCall = () => {
    if (webphoneRef.current) {
      webphoneRef.current.reject();
    }
  };

  // Toggle mute
  const toggleMute = () => {
    if (webphoneRef.current) {
      webphoneRef.current.mute(!isMuted, 2); // 2 = microphone
      setIsMuted(!isMuted);
    }
  };

  // Toggle hold
  const toggleHold = () => {
    if (webphoneRef.current) {
      webphoneRef.current.hold(!isOnHold);
      setIsOnHold(!isOnHold);
    }
  };

  // Send DTMF
  const sendDTMF = (digit) => {
    if (webphoneRef.current && callState === 'connected') {
      webphoneRef.current.dtmf(digit);
    }
  };

  // Dial pad click
  const handleDialPadClick = (digit) => {
    if (callState === 'connected') {
      sendDTMF(digit);
    } else {
      setDialNumber(prev => prev + digit);
    }
  };

  // Call from directory
  const callFromDirectory = (entry) => {
    makeCall(entry.extension);
  };

  // Call entered number
  const callEnteredNumber = () => {
    if (dialNumber.trim()) {
      makeCall(dialNumber.trim());
    }
  };

  // Connect to phone system
  const connectToPhoneSystem = () => {
    if (webphoneRef.current) {
      initializeMizuWebphone();
    } else {
      loadMizuWebphone();
    }
  };

  // Render registration status badge
  const renderStatusBadge = () => {
    if (isConnecting) {
      return (
        <Badge variant="secondary" className="bg-yellow-100 text-yellow-700 text-xs">
          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          Connecting
        </Badge>
      );
    }
    if (isRegistered) {
      return (
        <Badge variant="secondary" className="bg-green-100 text-green-700 text-xs">
          <CheckCircle className="h-3 w-3 mr-1" />
          Registered
        </Badge>
      );
    }
    if (registrationError) {
      return (
        <Badge variant="destructive" className="text-xs">
          <XCircle className="h-3 w-3 mr-1" />
          Not Registered
        </Badge>
      );
    }
    return (
      <Badge variant="secondary" className="bg-gray-100 text-gray-600 text-xs">
        <AlertCircle className="h-3 w-3 mr-1" />
        Offline
      </Badge>
    );
  };

  // Render dial pad
  const renderDialPad = () => {
    const keys = [
      ['1', '2', '3'],
      ['4', '5', '6'],
      ['7', '8', '9'],
      ['*', '0', '#']
    ];

    return (
      <div className="space-y-4">
        <Input
          value={dialNumber}
          onChange={(e) => setDialNumber(e.target.value)}
          placeholder="Enter number"
          className="text-center text-2xl font-mono tracking-wider"
          data-testid="dial-input"
        />
        <div className="grid grid-cols-3 gap-2">
          {keys.flat().map((key) => (
            <Button
              key={key}
              variant="outline"
              className="h-14 text-xl font-semibold hover:bg-gray-100"
              onClick={() => handleDialPadClick(key)}
              data-testid={`dial-key-${key}`}
            >
              {key}
            </Button>
          ))}
        </div>
        <Button
          className="w-full h-12 bg-green-600 hover:bg-green-700 text-white"
          onClick={callEnteredNumber}
          disabled={!dialNumber.trim() || !isRegistered}
          data-testid="call-button"
        >
          <Phone className="h-5 w-5 mr-2" />
          Call
        </Button>
      </div>
    );
  };

  // Render directory
  const renderDirectory = () => (
    <ScrollArea className="h-[300px]">
      <div className="space-y-2 pr-4">
        {directory.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Phone className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No directory entries</p>
          </div>
        ) : (
          directory.map((entry) => {
            const IconComponent = getDepartmentIcon(entry.department);
            return (
              <div
                key={entry.id}
                className="flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => isRegistered && callFromDirectory(entry)}
                data-testid={`directory-entry-${entry.extension}`}
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <IconComponent className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{entry.name}</p>
                    <p className="text-sm text-gray-500">
                      {entry.department && `${entry.department} • `}Ext: {entry.extension}
                    </p>
                  </div>
                </div>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  className="text-green-600 hover:text-green-700 hover:bg-green-50"
                  disabled={!isRegistered}
                >
                  <Phone className="h-4 w-4" />
                </Button>
              </div>
            );
          })
        )}
      </div>
    </ScrollArea>
  );

  // Render call history
  const renderCallHistory = () => (
    <ScrollArea className="h-[300px]">
      <div className="space-y-2 pr-4">
        {callLogs.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <History className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No call history</p>
          </div>
        ) : (
          callLogs.map((log) => {
            const isOutbound = log.direction === 'outbound';
            const isMissed = log.status === 'missed';
            const Icon = isMissed ? PhoneMissed : (isOutbound ? PhoneCall : PhoneIncoming);
            const iconColor = isMissed ? 'text-red-500' : (isOutbound ? 'text-blue-500' : 'text-green-500');
            
            return (
              <div
                key={log.id}
                className="flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50"
              >
                <div className="flex items-center gap-3">
                  <Icon className={`h-5 w-5 ${iconColor}`} />
                  <div>
                    <p className="font-medium text-gray-900">
                      {isOutbound ? log.callee_name || log.callee_id : log.caller_name || log.caller_id}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(log.started_at).toLocaleString()}
                      {log.duration_seconds > 0 && ` • ${formatDuration(log.duration_seconds)}`}
                    </p>
                  </div>
                </div>
                <Badge variant={isMissed ? "destructive" : "secondary"}>
                  {log.status}
                </Badge>
              </div>
            );
          })
        )}
      </div>
    </ScrollArea>
  );

  // Render active call UI
  const renderActiveCall = () => (
    <div className="text-center space-y-4">
      {/* Caller info */}
      <div className="py-6">
        <div className="h-20 w-20 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
          <Phone className="h-10 w-10 text-blue-600" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900">{callerInfo?.name || 'Unknown'}</h3>
        <p className="text-gray-500">{callerInfo?.number}</p>
        {callState === 'connected' && (
          <p className="text-lg font-mono text-green-600 mt-2">{formatDuration(callDuration)}</p>
        )}
        {callState === 'ringing' && (
          <p className="text-orange-500 animate-pulse">
            {callerInfo?.direction === 'inbound' ? 'Incoming call...' : 'Calling...'}
          </p>
        )}
      </div>

      {/* Call controls */}
      <div className="flex justify-center gap-4">
        {callState === 'ringing' && callerInfo?.direction === 'inbound' ? (
          <>
            <Button
              size="lg"
              className="h-14 w-14 rounded-full bg-green-600 hover:bg-green-700"
              onClick={acceptCall}
              data-testid="answer-call"
            >
              <Phone className="h-6 w-6" />
            </Button>
            <Button
              size="lg"
              variant="destructive"
              className="h-14 w-14 rounded-full"
              onClick={rejectCall}
              data-testid="reject-call"
            >
              <PhoneOff className="h-6 w-6" />
            </Button>
          </>
        ) : callState === 'connected' ? (
          <>
            <Button
              size="lg"
              variant={isMuted ? "destructive" : "outline"}
              className="h-12 w-12 rounded-full"
              onClick={toggleMute}
              data-testid="mute-button"
            >
              {isMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
            </Button>
            <Button
              size="lg"
              variant={isOnHold ? "secondary" : "outline"}
              className="h-12 w-12 rounded-full"
              onClick={toggleHold}
              data-testid="hold-button"
            >
              {isOnHold ? <Play className="h-5 w-5" /> : <Pause className="h-5 w-5" />}
            </Button>
            <Button
              size="lg"
              variant="destructive"
              className="h-14 w-14 rounded-full"
              onClick={hangupCall}
              data-testid="hangup-button"
            >
              <PhoneOff className="h-6 w-6" />
            </Button>
          </>
        ) : (
          <Button
            size="lg"
            variant="destructive"
            className="h-14 w-14 rounded-full"
            onClick={hangupCall}
            data-testid="cancel-call"
          >
            <PhoneOff className="h-6 w-6" />
          </Button>
        )}
      </div>

      {/* DTMF Keypad (during call) */}
      {callState === 'connected' && (
        <div className="pt-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setActiveTab('dialpad')}
            className="text-gray-500"
          >
            <Hash className="h-4 w-4 mr-1" />
            Keypad
          </Button>
        </div>
      )}
    </div>
  );

  // If not configured
  if (!sipConfig && !loading) {
    return null;
  }

  return (
    <>
      {/* Floating button */}
      {!isOpen && (
        <Button
          onClick={() => {
            setIsOpen(true);
            if (!webphoneLoaded && sipConfig && guestCredentials) {
              loadMizuWebphone();
            }
          }}
          className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg bg-green-600 hover:bg-green-700 z-50"
          data-testid="open-dialer"
        >
          <Phone className="h-6 w-6" />
        </Button>
      )}

      {/* Dialer panel */}
      {isOpen && (
        <Card 
          className={`fixed z-50 shadow-2xl transition-all duration-200 ${
            isMinimized 
              ? 'bottom-6 right-6 w-72' 
              : 'bottom-6 right-6 w-96'
          }`}
          data-testid="dialer-panel"
        >
          <CardHeader className="pb-2 flex flex-row items-center justify-between bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg">
            <div className="flex items-center gap-2">
              <Phone className="h-5 w-5" />
              <CardTitle className="text-base">Phone</CardTitle>
              {renderStatusBadge()}
            </div>
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0 text-white hover:bg-white/20"
                onClick={() => setIsMinimized(!isMinimized)}
              >
                {isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0 text-white hover:bg-white/20"
                onClick={() => {
                  setIsOpen(false);
                  onClose?.();
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>

          {!isMinimized && (
            <CardContent className="pt-4">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
                </div>
              ) : callState !== 'idle' ? (
                renderActiveCall()
              ) : (
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="directory" data-testid="tab-directory">
                      <Users className="h-4 w-4 mr-1" />
                      Directory
                    </TabsTrigger>
                    <TabsTrigger value="dialpad" data-testid="tab-dialpad">
                      <Hash className="h-4 w-4 mr-1" />
                      Dial
                    </TabsTrigger>
                    <TabsTrigger value="history" data-testid="tab-history">
                      <History className="h-4 w-4 mr-1" />
                      History
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="directory" className="mt-4">
                    {renderDirectory()}
                  </TabsContent>
                  <TabsContent value="dialpad" className="mt-4">
                    {renderDialPad()}
                  </TabsContent>
                  <TabsContent value="history" className="mt-4">
                    {renderCallHistory()}
                  </TabsContent>
                </Tabs>
              )}

              {/* Connection status and connect button */}
              {!isRegistered && !loading && sipConfig && (
                <div className="mt-4 pt-4 border-t">
                  {registrationError && (
                    <p className="text-sm text-red-500 mb-2 text-center">{registrationError}</p>
                  )}
                  <Button
                    className="w-full"
                    onClick={connectToPhoneSystem}
                    disabled={isConnecting}
                    data-testid="connect-sip"
                  >
                    {isConnecting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Connecting...
                      </>
                    ) : (
                      'Connect to Phone System'
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          )}

          {/* Minimized view during call */}
          {isMinimized && callState !== 'idle' && (
            <CardContent className="py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`h-3 w-3 rounded-full ${callState === 'connected' ? 'bg-green-500' : 'bg-orange-500 animate-pulse'}`} />
                  <span className="text-sm font-medium">{callerInfo?.name}</span>
                </div>
                {callState === 'connected' && (
                  <span className="text-sm font-mono text-green-600">{formatDuration(callDuration)}</span>
                )}
                <Button
                  size="sm"
                  variant="destructive"
                  className="h-8 w-8 p-0 rounded-full"
                  onClick={hangupCall}
                >
                  <PhoneOff className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          )}
        </Card>
      )}
    </>
  );
};

export default MizuDialer;
